import useAppDispatch from "../../../../../hooks/redux/base/useAppDispatch";
import useAppSelector from "../../../../../hooks/redux/base/useAppSelector";
import {
  AsyncAutocompleteWithAdd,
  AsyncCallArgs,
  CircularLoader,
  FormSection,
  GridContainer,
  ReloadWatcherObject,
  SimpleConfirmDialog,
  useAsyncCall,
  useReloadWatcherReader,
  ViewDialog,
} from "@duosoftbg/nacid-components";
import { closeModal } from "../../../../../store/redux/slice/ComponentsControl/groupsControl";
import GroupMainDetailsForm from "./parts/GroupMainDetailsForm";
import React, { useEffect, useState } from "react";
import {
  addRoleToGroup,
  addUserToGroup,
  getFullGroupDetails,
  getRolesWithAdditionalParamsAutocomplete,
  getUsersWithAdditionalParamsAutocomplete,
  removeRoleFromGroup,
  removeUserFromGroup,
} from "../../../../../axios/api/services";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import RolesDialogList from "./parts/RolesDialogList";
import UsersDialogList from "../../users/dialog/parts/UsersDialogList";
import ScrollableGridContainer from "../../../common/grid/ScrollableGridContainer";
import GroupHierarchyContent from "./parts/GroupHierarchyContent";
import InheritedRolesContent from "./parts/InheritedRolesContent";

const GroupEditDialog = ({ filterFn }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { asyncCall } = useAsyncCall();
  const { reloadWatcher } = useReloadWatcherReader(ReloadWatcherObject.build("groupMainDetails", "change"));

  const { open, id } = useAppSelector((state) => {
    return state.ComponentsControl.groupsControl.modals.edit;
  });

  const [group, setGroup] = useState(null);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [removeUser, setRemoveUser] = useState(null);
  const [removeRole, setRemoveRole] = useState(null);
  const [confirmModalOpenUser, setConfirmModalOpenUser] = useState(false);
  const [confirmModalOpenRole, setConfirmModalOpenRole] = useState(false);

  useEffect(() => {
    if (open && id) {
      const getGroupCallArgs: AsyncCallArgs = {
        promise: getFullGroupDetails(id),
        onSuccess: (response) => {
          setGroup(response);
          setUsers(response.users);
          setRoles(response.roles);
        },
        onError: () => {
          setGroup(null);
          dispatch(closeModal({ modalType: "edit" }));
        },
      };
      asyncCall(getGroupCallArgs);
    }
  }, [open, id, asyncCall, dispatch, t, reloadWatcher]);

  const handleRemoveUserClick = (user) => {
    setRemoveUser(user);
    setConfirmModalOpenUser(true);
  };

  const handleRemoveRoleClick = (role) => {
    setRemoveRole(role);
    setConfirmModalOpenRole(true);
  };

  const handleRemoveUserFromGroup = () => {
    const removeUserCallArgs: AsyncCallArgs = {
      promise: removeUserFromGroup(group.group.id, removeUser.id),
      onSuccess: (response) => {
        if (response) {
          const newUsers = users.filter((listUser) => removeUser.id !== listUser.id);
          setUsers(newUsers);
          setRemoveUser(null);
          setConfirmModalOpenUser(false);
          toast.success(t("m.group.user.remove.success"));
        }
      },
    };
    asyncCall(removeUserCallArgs);
  };

  const handleRemoveRoleFromGroup = () => {
    const removeRoleCallArgs: AsyncCallArgs = {
      promise: removeRoleFromGroup(group.group.id, removeRole.id),
      onSuccess: (response) => {
        if (response) {
          const newRoles = roles.filter((listRole) => removeRole.id !== listRole.id);
          setRoles(newRoles);
          setRemoveRole(null);
          setConfirmModalOpenRole(false);
          toast.success(t("m.group.role.remove.success"));
        }
      },
    };
    asyncCall(removeRoleCallArgs);
  };

  const handleAddUserToGroup = (user) => {
    if (!users.find((arrayUser) => arrayUser.id === user.id)) {
      const addUserCallArgs: AsyncCallArgs = {
        promise: addUserToGroup(group.group.id, user.id),
        onSuccess: (response) => {
          if (response) {
            const newUsers = [...users, user];
            setUsers(newUsers);
            toast.success(t("m.group.user.add.success"));
          }
        },
      };
      asyncCall(addUserCallArgs);
    }
  };

  const handleAddRoleToGroup = (role) => {
    if (!roles.find((arrayRole) => arrayRole.id === role.id)) {
      const addRoleCallArgs: AsyncCallArgs = {
        promise: addRoleToGroup(group.group.id, role.id),
        onSuccess: (response) => {
          if (response) {
            const newRoles = [...roles, role];
            setRoles(newRoles);
            toast.success(t("m.group.role.add.success"));
          }
        },
      };
      asyncCall(addRoleCallArgs);
    }
  };

  return (
    <div>
      <SimpleConfirmDialog
        open={confirmModalOpenUser}
        setOpen={setConfirmModalOpenUser}
        dialogTitleText={"m.confirm"}
        alertText={"m.group.user.remove.confirm"}
        alertType={"warning"}
        onConfirm={handleRemoveUserFromGroup}
      />
      <SimpleConfirmDialog
        open={confirmModalOpenRole}
        setOpen={setConfirmModalOpenRole}
        dialogTitleText={"m.confirm"}
        alertText={"m.group.role.remove.confirm"}
        alertType={"warning"}
        onConfirm={handleRemoveRoleFromGroup}
      />
      <ViewDialog
        open={open}
        onClose={() => {
          dispatch(closeModal({ modalType: "edit" }));
          setGroup(null);
        }}
        title={"t.group.edit.dialog"}
      >
        {!group && <CircularLoader />}
        {group && (
          <>
            <FormSection label={"t.hierarchy.details"}>
              <GroupHierarchyContent group={group} modalType={"edit"} />
            </FormSection>

            <FormSection label={"t.main.details"}>
              <GroupMainDetailsForm group={group.group} filterFn={filterFn} />
            </FormSection>

            {group?.group?.parentGroup?.id && (
              <FormSection label={"t.inherited.roles"}>
                <InheritedRolesContent group={group} />
              </FormSection>
            )}

            <FormSection label={"t.group.roles"}>
              <GridContainer spacing={3} mt={0}>
                <AsyncAutocompleteWithAdd
                  key={`roles-autocomplete-key-${users.length}`}
                  onAddRecord={handleAddRoleToGroup}
                  setOptionText={(option) => option.name}
                  getOptionLabel={(option) => option.id}
                  setInputOnSelect={(option) => option.name}
                  autocompleteFn={getRolesWithAdditionalParamsAutocomplete}
                  additionalParams={{ excludedGroupId: group.group.id }}
                  label={"l.select.role"}
                />
              </GridContainer>

              <ScrollableGridContainer>
                <RolesDialogList roles={roles} isEdit={true} onRemove={handleRemoveRoleClick} />
              </ScrollableGridContainer>
            </FormSection>

            <FormSection label={"t.group.users"}>
              <GridContainer spacing={3} mt={0}>
                <AsyncAutocompleteWithAdd
                  key={`users-autocomplete-key-${users.length}`}
                  onAddRecord={handleAddUserToGroup}
                  setOptionText={(option) => option.username + " (" + option.fullName + ")"}
                  getOptionLabel={(option) => option.id}
                  setInputOnSelect={(option) => option.username}
                  autocompleteFn={getUsersWithAdditionalParamsAutocomplete}
                  label={"l.select.user"}
                  additionalParams={{ excludedGroupId: group.group.id }}
                  reduceOptionObject={false}
                  sortColumn={"username"}
                />
              </GridContainer>

              <ScrollableGridContainer>
                <UsersDialogList users={users} isEdit={true} onRemove={handleRemoveUserClick} />
              </ScrollableGridContainer>
            </FormSection>
          </>
        )}
      </ViewDialog>
    </div>
  );
};

export default GroupEditDialog;
